'use client'

import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import MobileMenu from "./MobileMenu";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  isActiveLink,
  isActiveParentChaild,
} from "./../utils/linkActiveChecker";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

const Header1 = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  
  const [scrolled, setScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState("en");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  
  const langRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "ar", label: "العربية", flag: "🇦🇪" },
  ];

  // Auth State
  useEffect(() => {
    const token = localStorage.getItem('token_customer_silverjarz');
    const userData = localStorage.getItem('customer_silverjarz');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData) as User;
        setIsLoggedIn(true);
        setUser(parsedUser);
      } catch (error) {
        console.error("User parsing error:", error);
        localStorage.removeItem('token_customer_silverjarz');
        localStorage.removeItem('customer_silverjarz');
      }
    }
  }, []);

  // Language Setup
  useEffect(() => {
    const savedLang = localStorage.getItem('lang');
    if (savedLang) {
      setSelectedLang(savedLang);
      i18n.changeLanguage(savedLang);
      document.documentElement.dir = savedLang === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = savedLang;
    }
  }, [i18n]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLanguageChange = (code: string) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = code;
    setIsLangOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token_customer_silverjarz');
    localStorage.removeItem('customer_silverjarz');
    setIsLoggedIn(false);
    setUser(null);
    setIsProfileOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const blogItems = [
    { name: t("visaApplication"), routePath: "/search-visa" },
    { name: t("trackApplication"), routePath: "/track-application" }
  ];

  const shengenItems = [
    { name: t("visaShengen"), routePath: "/schengen-visa" },
    { name: t("dummyTicket"), routePath: "/dummy-ticket" },
    { name: t("insurance"), routePath: "/insurance" }
  ];

  // Menu items pour "Other Countries"
  const countryVisaItems = [
    { name: t("oman"), routePath: "/visa-oman" },
    { name: t("uae"), routePath: "/visa-uae" },
    { name: t("saudiArabia"), routePath: "/visa-ksa" },
    { name: t("bahrain"), routePath: "/visa-bahrain" },
    { name: t("kuwait"), routePath: "/visa-kuwait" },
    { name: t("qatar"), routePath: "/visa-qatar" },
    { name: t("thailand"), routePath: "/visa-thailand" },
    { name: t("india"), routePath: "/visa-india" },
    { name: t("armenia"), routePath: "/visa-armenia" },
    { name: t("usa"), routePath: "/visa-usa" },
    { name: t("canada"), routePath: "/visa-canada" },
  ];

  const currentLang = languages.find((l) => l.code === selectedLang);

  const getUserInitial = (): string => {
    if (user?.name) return user.name.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  const getUserFirstName = (): string => {
    if (user?.name) return user.name.split(' ')[0];
    return 'User';
  };

  const isActive = (path: string) => pathname === path;

  return (
    <>
      <style>{`
        /* Fixed Header Layout Container */
        .creative-header-wrapper {
          position: fixed;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 48px);
          max-width: 1320px;
          z-index: 1000;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .creative-header-wrapper.is-scrolled {
          top: 8px;
          width: calc(100% - 24px);
        }

        .creative-header-card {
          background: rgba(20, 38, 48, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 50px;
          padding: 10px 24px;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
          
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .brand-logo img {
          height: 38px;
          width: auto;
          display: block;
        }

        /* Nav Container */
        .creative-nav-container {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .creative-nav-list {
          display: flex;
          align-items: center;
          gap: 6px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .nav-item-dropdown {
          position: relative;
        }

        .creative-nav-link {
          position: relative;
          color: rgba(255, 255, 255, 0.85) !important;
          font-weight: 500;
          font-size: 14px;
          padding: 8px 16px;
          border-radius: 20px;
          transition: all 0.25s ease;
          text-decoration: none !important;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          background: transparent;
          border: none;
          cursor: pointer;
        }

        .creative-nav-link:hover, 
        .nav-item-dropdown:hover > .creative-nav-link {
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.08);
        }

        .creative-nav-link.active-link {
          color: #ffffff !important;
          font-weight: 600;
        }

        .creative-nav-link.active-link::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 14px;
          height: 2px;
          background: linear-gradient(90deg, #d3be76, #b8a258);
          border-radius: 2px;
        }

        /* Badge Promo */
        .badge-promo {
          background: linear-gradient(135deg, #e65c00, #F9D423);
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 10px;
          text-transform: uppercase;
        }

        /* Fixed Dropdown Styling */
        .creative-subnav {
          position: absolute;
          top: calc(100% + 12px);
          left: 0;
          min-width: 190px;
          background: rgba(20, 38, 48, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(211, 190, 118, 0.25);
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
          padding: 8px;
          margin: 0;
          list-style: none;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 1050;
        }

        .nav-item-dropdown:hover .creative-subnav {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .creative-subnav.grid-2col {
          width: 420px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px;
          left: 50%;
          transform: translateX(-50%) translateY(10px);
        }

        .nav-item-dropdown:hover .creative-subnav.grid-2col {
          transform: translateX(-50%) translateY(0);
        }

        .creative-subnav::before {
          content: '';
          position: absolute;
          top: -12px;
          left: 0;
          width: 100%;
          height: 12px;
        }

        .creative-subnav li a {
          display: flex;
          flex-direction: column;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 500;
          color: #ffffff;
          text-decoration: none;
          border-radius: 10px;
          transition: all 0.2s ease;
          white-space: nowrap;
          gap: 2px;
        }

        .creative-subnav li a .sub-desc {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
          font-weight: 400;
        }

        .creative-subnav li a:hover {
          background: rgba(211, 190, 118, 0.12);
          color: #ffffff;
        }

        /* Right Actions Controls */
        .creative-actions-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        /* Language Pill */
        .creative-pill-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 30px;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 500;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }

        .creative-pill-btn:hover {
          border-color: #d3be76;
          background: rgba(255, 255, 255, 0.08);
        }

        /* Connect Button */
        .creative-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: #0e1e26 !important;
          background: linear-gradient(135deg, #d3be76 0%, #b09a50 100%);
          text-decoration: none !important;
          transition: all 0.25s ease;
          white-space: nowrap;
        }

        .creative-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(211, 190, 118, 0.3);
          color: #0e1e26 !important;
        }

        /* Dropdown Box */
        .creative-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: rgba(20, 38, 48, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(211, 190, 118, 0.25);
          border-radius: 16px;
          box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.4);
          padding: 8px;
          min-width: 160px;
          z-index: 1050;
        }

        .dropdown-item-creative {
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          color: #ffffff;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .dropdown-item-creative:hover {
          background: rgba(211, 190, 118, 0.12);
          color: #ffffff;
        }

        .dropdown-item-creative.danger {
          color: #ef4444;
        }

        .dropdown-item-creative.danger:hover {
          background: rgba(239, 68, 68, 0.12);
        }

        .user-pill-avatar {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d3be76, #b09a50);
          color: #0e1e26;
          font-weight: 700;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Profile Dropdown Header */
        .profile-dropdown-head {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 6px 8px 14px;
        }

        .profile-avatar-lg {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          flex-shrink: 0;
          background: linear-gradient(135deg, #d3be76, #b09a50);
          color: #0e1e26;
          font-weight: 700;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-name {
          color: #ffffff;
          font-size: 13.5px;
          font-weight: 600;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .profile-email {
          color: rgba(255, 255, 255, 0.5);
          font-size: 11.5px;
          margin-top: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dropdown-divider {
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
          margin: 4px 6px 8px;
        }

        .dropdown-item-icon {
          width: 26px;
          height: 26px;
          border-radius: 8px;
          flex-shrink: 0;
          background: rgba(211, 190, 118, 0.12);
          color: #d3be76;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dropdown-item-label {
          font-size: 13px;
          font-weight: 500;
          color: #ffffff;
        }

        @media (max-width: 991px) {
          .creative-header-wrapper {
            width: calc(100% - 20px);
            top: 10px;
          }
          .creative-header-card {
            border-radius: 20px;
          }
          .creative-nav-list {
            display: none;
          }
        }
      `}</style>

      <div className={`creative-header-wrapper ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="creative-header-card">
          
          {/* Logo & Navigation */}
          <div className="creative-nav-container">
            <a href="/" className="brand-logo">
              <img src="/img/logo_white.png" alt="SilverJarz Logo" />
            </a>

            <div className="header-menu xl:d-none">
              <ul className="creative-nav-list">
                <li>
                  <a href="/" className={`creative-nav-link ${isActive("/") ? "active-link" : ""}`}>
                    {t("home")}
                  </a>
                </li>

                {/* Visas Dropdown */}
                <li className="nav-item-dropdown">
                  <a href="#" className={`creative-nav-link ${isActiveParentChaild(blogItems, pathname) ? "active-link" : ""}`}>
                    <span>{t("visas")}</span>
                    <i className="icon icon-chevron-sm-down text-10" />
                  </a>
                  <ul className="creative-subnav">
                    {blogItems.map((menu, i) => (
                      <li key={i}>
                        <a href={menu.routePath}>
                          {menu.name}
                          <span className="sub-desc">
                            {i === 0 ? t("visaApplicationDesc") : t("trackApplicationDesc")}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Schengen Dropdown */}
                <li className="nav-item-dropdown">
                  <a href="#" className={`creative-nav-link ${isActiveParentChaild(shengenItems, pathname) ? "active-link" : ""}`}>
                    <span>{t("schengen")}</span>
                    <i className="icon icon-chevron-sm-down text-10" />
                  </a>
                  <ul className="creative-subnav">
                    {shengenItems.map((menu, i) => (
                      <li key={i}>
                        <a href={menu.routePath}>
                          {menu.name}
                          <span className="sub-desc">
                            {i === 0 ? t("schengenVisaDesc") : i === 1 ? t("dummyTicketDesc") : t("travelInsuranceDesc")}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Other Countries Dropdown */}
                <li className="nav-item-dropdown">
                  <a href="#" className={`creative-nav-link ${isActiveParentChaild(countryVisaItems, pathname) ? "active-link" : ""}`}>
                    <span>{t("otherCountries")}</span>
                    <i className="icon icon-chevron-sm-down text-10" />
                  </a>
                  <ul className="creative-subnav grid-2col">
                    {countryVisaItems.map((menu, i) => (
                      <li key={i}>
                        <a href={menu.routePath}>
                          {menu.name}
                          <span className="sub-desc">{t("visaFor")} {menu.name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                <li>
                  <a href="/trips" className={`creative-nav-link ${isActive("/trips") ? "active-link" : ""}`}>
                    {t("tours")}
                  </a>
                </li>

                <li>
                  <a href="/services" className={`creative-nav-link ${isActive("/services") ? "active-link" : ""}`}>
                    Services
                    <span className="badge-promo">{t("hot")}</span>
                  </a>
                </li>

                <li>
                  <a href="/flights" className={`creative-nav-link ${isActive("/flights") ? "active-link" : ""}`}>
                    {t("flights")}
                  </a>
                </li>

                <li>
                  <a href="/car-rent" className={`creative-nav-link ${isActive("/car-rent") ? "active-link" : ""}`}>
                    {t("carRental")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Controls */}
          <div className="creative-actions-container">
            
            {/* Language Dropdown */}
            <div className="relative" ref={langRef}>
              <button
                className="creative-pill-btn"
                onClick={() => setIsLangOpen((prev) => !prev)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d3be76" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                <span>{currentLang?.label}</span>
                <i className={`icon icon-chevron-sm-down text-10 transition-transform ${isLangOpen ? "rotate-180" : ""}`} />
              </button>

              {isLangOpen && (
                <div className="creative-dropdown">
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className="dropdown-item-creative"
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <span>{lang.flag} {lang.label}</span>
                      {lang.code === selectedLang && (
                        <i className="icon icon-check text-12" style={{ color: '#d3be76' }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Auth Button or User Profile */}
            {isLoggedIn && user ? (
              <div className="relative" ref={profileRef}>
                <button
                  className="creative-pill-btn"
                  onClick={() => setIsProfileOpen((prev) => !prev)}
                  style={{ padding: '4px 12px 4px 4px', border: '1px solid #d3be76' }}
                >
                  <div className="user-pill-avatar">
                    {getUserInitial()}
                  </div>
                  <span className="font-medium text-13">{getUserFirstName()}</span>
                  <i className={`icon icon-chevron-sm-down text-10 transition-transform ${isProfileOpen ? "rotate-180" : ""}`} />
                </button>

                {isProfileOpen && (
                  <div className="creative-dropdown" style={{ minWidth: '200px' }}>
                    <div className="profile-dropdown-head">
                      <div className="profile-avatar-lg">{getUserInitial()}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="profile-name">{user.name}</div>
                        <div className="profile-email">{user.email}</div>
                      </div>
                    </div>

                    <div className="dropdown-divider" />

                    <div className="dropdown-item-creative" onClick={() => { setIsProfileOpen(false); navigate('/my-dashboard'); }}>
                      <span>{t("dashboard")}</span>
                    </div>
                    <div className="dropdown-item-creative" onClick={() => { setIsProfileOpen(false); navigate('/my-bookings'); }}>
                      <span>{t("bookings")}</span>
                    </div>
                    <div className="dropdown-item-creative" onClick={() => { setIsProfileOpen(false); navigate('/profile-settings'); }}>
                      <span>{t("settings")}</span>
                    </div>

                    <div className="dropdown-divider" />

                    <div className="dropdown-item-creative danger" onClick={handleLogout}>
                      <span>{t("signOut")}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a href="/login" className="creative-cta-btn">
                <span>{t("signIn")}</span>
              </a>
            )}

            {/* Mobile Menu Icon */}
            <div className="d-none xl:d-flex items-center">
              <button
                className="creative-pill-btn"
                data-bs-toggle="offcanvas"
                aria-controls="mobile-sidebar_menu"
                data-bs-target="#mobile-sidebar_menu"
                style={{ padding: '8px 12px' }}
              >
                <i className="icon icon-menu text-16" />
              </button>
              <div
                className="offcanvas offcanvas-start mobile_menu-contnet"
                tabIndex={-1}
                id="mobile-sidebar_menu"
                aria-labelledby="offcanvasMenuLabel"
                data-bs-scroll="true"
              >
                <MobileMenu />
              </div>
            </div>

          </div>

        </div>
      </div>
    </>
  );
};

export default Header1;